// src/components/proposal-formats/FormatPaymentMilestonesSection.tsx

import { useCallback, useEffect, useMemo, useState } from "react";
import type { OpportunityFormatKey } from "@/types/opportunity";
import {
  deleteFormatPaymentMilestone,
  listFormatPaymentMilestones,
  reorderFormatPaymentMilestones,
  upsertFormatPaymentMilestone,
} from "@/lib/opportunity/formatPaymentMilestoneClient";
import {
  isPersistedPaymentMilestoneId,
  mergePaymentMilestonesForEditor,
  PAYMENT_LINE_ITEM_BASE_REF,
  type FormatPaymentMilestone,
} from "@/lib/opportunity/formatPaymentMilestoneTypes";
import {
  buildPaymentLineAmountMap,
  evaluatePaymentMilestones,
} from "@/lib/opportunity/formatPaymentMilestoneEval";
import type { FormatCalcLine } from "@/lib/opportunity/formatCalcLineTypes";
import { evaluateFormatCalcLines } from "@/lib/opportunity/formatCalcEval";
import type { DepositComputation } from "@/lib/opportunity/formatDepositEval";
import { FormatPaymentMilestoneRow, type PaymentLineItemOption } from "./FormatPaymentMilestoneRow";
import { ProposalFormatCollapsibleSection } from "./ProposalFormatCollapsibleSection";

const PREVIEW_TOTAL = 10_000;

export type FormatPaymentMilestonesSectionProps = {
  formatKey: OpportunityFormatKey;
  productType: string;
  calcLines: FormatCalcLine[];
  baseAmountLabel: string;
  depositPreview: DepositComputation;
  reloadTick: number;
  onChanged: () => void;
};

export function FormatPaymentMilestonesSection(props: FormatPaymentMilestonesSectionProps) {
  const { formatKey, productType, calcLines, baseAmountLabel, depositPreview, reloadTick, onChanged } =
    props;
  const [milestones, setMilestones] = useState<FormatPaymentMilestone[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await listFormatPaymentMilestones({ format_key: formatKey, product_type: productType });
    setLoading(false);
    if (r.ok) {
      const merged = mergePaymentMilestonesForEditor(
        r.data.milestones.map((m) => ({ ...m, format_key: formatKey, product_type: productType })),
      );
      setMilestones(merged);
    }
  }, [formatKey, productType]);

  useEffect(() => {
    void load();
  }, [load, reloadTick]);

  const displayMilestones = useMemo(() => {
    return [...milestones].sort((a, b) => a.sort_order - b.sort_order);
  }, [milestones]);

  const lineItemOptions = useMemo<PaymentLineItemOption[]>(() => {
    const opts: PaymentLineItemOption[] = [
      { ref: PAYMENT_LINE_ITEM_BASE_REF, label: baseAmountLabel || "Base amount" },
    ];
    for (const line of calcLines) {
      opts.push({ ref: line.id, label: line.label });
    }
    return opts;
  }, [baseAmountLabel, calcLines]);

  const previewRows = useMemo(() => {
    const calcEval = evaluateFormatCalcLines({ lines: calcLines, baseSubtotal: PREVIEW_TOTAL });
    const lineAmounts = buildPaymentLineAmountMap({
      baseAmount: PREVIEW_TOTAL,
      evaluatedCalcLines: [...calcEval.before, ...calcEval.after],
    });
    const defs = displayMilestones.map((m) => ({
      milestone_role: m.milestone_role,
      sort_order: m.sort_order,
      label: m.label,
      subtext: m.subtext,
      amount_mode: m.amount_mode,
      default_amount: m.default_amount,
      line_item_ref: m.line_item_ref,
    }));
    return evaluatePaymentMilestones({
      milestones: defs,
      depositAmount: depositPreview.depositAmount,
      totalSalePrice: PREVIEW_TOTAL,
      remainingBalance: PREVIEW_TOTAL - depositPreview.depositAmount,
      lineAmounts,
    });
  }, [displayMilestones, depositPreview.depositAmount, calcLines]);

  const previewByRoleOrder = useMemo(() => {
    const map = new Map<string, number>();
    displayMilestones.forEach((m, idx) => {
      map.set(m.id, previewRows[idx]?.amount ?? 0);
    });
    return map;
  }, [displayMilestones, previewRows]);

  async function addCustomLine() {
    const customs = displayMilestones.filter((m) => m.milestone_role === "custom");
    const nextOrder =
      customs.length > 0 ? Math.max(...customs.map((m) => m.sort_order)) + 1 : 1;
    const r = await upsertFormatPaymentMilestone({
      format_key: formatKey,
      product_type: productType,
      milestone_role: "custom",
      sort_order: nextOrder,
      label: "New milestone",
      subtext: null,
      amount_mode: "percent_of_remaining",
      default_amount: 10,
      line_item_ref: null,
    });
    if (r.ok) onChanged();
  }

  async function moveLine(id: string, direction: "up" | "down") {
    const idx = displayMilestones.findIndex((m) => m.id === id);
    if (idx < 0) return;
    const line = displayMilestones[idx];
    if (line.milestone_role !== "custom") return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= displayMilestones.length) return;
    if (displayMilestones[swapIdx].milestone_role !== "custom") return;

    const reordered = [...displayMilestones];
    const tmp = reordered[idx];
    reordered[idx] = reordered[swapIdx];
    reordered[swapIdx] = tmp;

    const persistedIds = reordered
      .map((m) => m.id)
      .filter((mid) => isPersistedPaymentMilestoneId(mid));
    if (persistedIds.length === 0) return;

    const r = await reorderFormatPaymentMilestones({
      format_key: formatKey,
      product_type: productType,
      ordered_ids: persistedIds,
    });
    if (r.ok) onChanged();
  }

  return (
    <ProposalFormatCollapsibleSection
      title="Payment schedule"
      description="Milestone labels and intermediate payments. Deposit amount comes from deposit rules above."
      headerRight={
        <button
          type="button"
          onClick={() => void addCustomLine()}
          className="px-2 py-1 rounded-lg border border-gray-300 text-[11px] hover:bg-gray-50 dark:border-zinc-600 dark:hover:bg-zinc-800 shrink-0"
        >
          + Insert milestone
        </button>
      }
    >
      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-2">
          {displayMilestones.map((milestone) => {
            const customs = displayMilestones.filter((m) => m.milestone_role === "custom");
            const customIdx = customs.findIndex((m) => m.id === milestone.id);
            return (
              <FormatPaymentMilestoneRow
                key={milestone.id}
                milestone={milestone}
                previewAmount={previewByRoleOrder.get(milestone.id) ?? null}
                lineItemOptions={lineItemOptions}
                canMoveUp={milestone.milestone_role === "custom" && customIdx > 0}
                canMoveDown={
                  milestone.milestone_role === "custom" && customIdx >= 0 && customIdx < customs.length - 1
                }
                onSave={async (payload) => {
                  const r = await upsertFormatPaymentMilestone(payload);
                  if (r.ok) onChanged();
                  return r.ok ? { ok: true } : { ok: false, error: r.error };
                }}
                onDelete={
                  milestone.milestone_role === "custom"
                    ? async (id) => {
                        const r = await deleteFormatPaymentMilestone(id);
                        if (r.ok) onChanged();
                        return r.ok ? { ok: true } : { ok: false, error: r.error };
                      }
                    : undefined
                }
                onMove={(dir) => void moveLine(milestone.id, dir)}
              />
            );
          })}
        </div>
      )}
    </ProposalFormatCollapsibleSection>
  );
}
